import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AssessmentOverviewAdapter } from 'components/Assessments/FuaAssessment/sections/AssessmentOverview/adapter'
import { SkilledNeedsAdapter } from 'components/Assessments/FuaAssessment/sections/SkilledNeeds/adapter'
import { ADLNeedsAdapter } from 'components/Assessments/FuaAssessment/sections/ADLNeeds/adapter'
import { DischargePlanningAdapter } from 'components/Assessments/FuaAssessment/sections/DischargePlanning/adapter'
import { AuthorizationAdapter } from 'components/Assessments/FuaAssessment/sections/Authorization/adapter'
import { PlanOfCareAdapter } from 'components/Assessments/FuaAssessment/sections/PlanOfCare/adapter'
import { VisitSchedulingAdapter } from 'components/Assessments/FuaAssessment/sections/VisitScheduling/adapter'
import { MedicationsAdapter } from 'components/Assessments/FuaAssessment/sections/Medications/adapter'
import { VitalsAdapter } from 'components/Assessments/FuaAssessment/sections/Vitals/adapter'
import { MedicalBackgroundAdapter } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/adapter'
import { SupervisoryReviewAdapter } from 'components/Assessments/FuaAssessment/sections/SupervisoryReview/adapter'
import { NarrativeAdapter } from 'components/Assessments/FuaAssessment/sections/Narrative/adapter'
import { FinalStepsAdapter } from 'components/Assessments/FuaAssessment/sections/FinalSteps/adapter'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { BaseSubsectionAdapter } from './BaseSubsectionAdapter'
import { BaseAssessmentAdapter } from './BaseAssessmentAdapter'
import { AdapterOptions, SectionAdapter } from './types'
import { AssessmentType } from '../../Clinical/Assessments/constants'
import { mutateFlatAnswers } from '../helpers'

/**
 * Array of all FUA section adapter instances
 */
const FUA_SECTION_ADAPTERS: BaseSubsectionAdapter<FuaFormData>[] | SectionAdapter<FuaFormData>[] = [
  new AssessmentOverviewAdapter(),
  new SkilledNeedsAdapter(),
  new ADLNeedsAdapter(),
  new DischargePlanningAdapter(),
  new AuthorizationAdapter(),
  new PlanOfCareAdapter(),
  new VisitSchedulingAdapter(),
  new MedicationsAdapter(),
  new VitalsAdapter(),
  new MedicalBackgroundAdapter(),
  new SupervisoryReviewAdapter(),
  new NarrativeAdapter(),
  new FinalStepsAdapter()
]

export class FuaAssessmentAdapter extends BaseAssessmentAdapter<FuaFormData> {
  readonly assessmentType = AssessmentType.FUA

  protected flatAnswersToFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): FuaFormData {
    const formData: Partial<FuaFormData> = {}

    for (const adapter of FUA_SECTION_ADAPTERS) {
      const sectionData = adapter.extractFormData(flatAnswers, options)
      Object.assign(formData, sectionData)
    }

    return formData as FuaFormData
  }

  protected formDataToFlatAnswers(
    formData: FuaFormData,
    currentFlatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Record<string, QuestionnaireResponseItem> {
    // Use composable section adapters for clean, maintainable transformation
    const allMutations: Array<{ linkId: string; value: unknown }> = []

    // Build mutations from all section adapters dynamically
    for (const adapter of FUA_SECTION_ADAPTERS) {
      if (adapter instanceof BaseSubsectionAdapter) {
        adapter.setFormData(formData)
        allMutations.push(...adapter.buildFlatAnswers())
      } else {
        allMutations.push(...adapter.buildFlatAnswers(formData))
      }
    }

    // Apply all mutations using existing helper
    mutateFlatAnswers(currentFlatAnswers, allMutations)

    return currentFlatAnswers
  }
}
